/**
 * The chosen granny flat plan, as the plan page, the family guide and Home read it (granny flat rebuild,
 * 21 Oct 2026 — the village's rvChosenPlan.ts, the Support at home sahChosenPlan.ts).
 *
 * ONE GRANNY FLAT PLAN ON RECORD. Choosing another plan replaces the earlier one that carries a card; older
 * records saved before the rebuild (no card) are left as they are.
 *
 * The card the family chose is stored on the record — its figures, not entering's at the time, the plan in
 * words, the arrangement (what was given, to whom, what counts as a gift, homeowner status, the home) — so
 * the plan page, the family guide's brief and Home show exactly what was chosen, with no re-run between.
 */
import { withChosenPlan, type PlanRecordLike } from '../../strategy/chosenPlan.ts';
import { buildSoaGuaranteedIncome } from '../advice/soaGuaranteedIncome.ts';
import type { RiskKind } from '../../strategy/strategyRisks.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { GfGuaranteedIncome } from './gfGuaranteedIncome.ts';
import type { GfSolicitorBriefFigures } from './gfSolicitorBrief.ts';

export interface GfFigures { legacy: number; pension: number; cashflow: number }

export interface GfArrangementRecord {
    /** Everything given for the right: cash, the home's value and the build. */
    given: number;
    cash: number;
    homeSignedOver: number;
    build: number;
    /** What counts as a gift for five years. */
    gift: number;
    /** Centrelink's value of the right: what was given, less what counts as a gift. */
    interestValue: number;
    /** Centrelink's formula value at the start date (the most cash can pay with no gift). */
    reasonableValue: number;
    isHomeowner: boolean;
    home: 'kept' | 'rented' | 'sold' | 'transferred' | null;
    /** The kept home's rent a year, after the weeks it sits empty (0 unless let). */
    rentAYear: number;
    /** YYYY-MM-DD; blank = starting now. */
    dateEntered: string;
    /** Who they live with, as step 1 answered it when the plan was chosen. */
    hostKind: 'child' | 'family' | 'friend' | null;
}

export interface GfFamilyRecord {
    personName: string;
    amount: number;
    kind: 'fullBack' | 'moneyBack' | 'incomeOnly';
    incomeAYear: number | null;
    compareNote: string | null;
    rows: Array<{ label: string; fromAnnuity: number; withPlan: number | null; without: number | null }>;
}

export interface GfPlanCardRecord {
    plan: GfFigures;
    baseline: GfFigures | null;
    words: { title: string; sub: string };
    arrangement: GfArrangementRecord;
    horizonYears: number;
    riskKinds: RiskKind[];
    /** 'engine' — one of the tested plans; 'manual' — built by hand. */
    origin: 'engine' | 'manual';
    /** A plan with a guaranteed income: what the family receives if death comes early, frozen when chosen. */
    family?: GfFamilyRecord | null;
}

export interface ChosenGfPlan extends PlanRecordLike {
    id: string;
    name?: string;
    displayName?: string;
    createdAt?: string;
    soaSnapshot?: unknown;
    source?: string;
    inputs?: { card?: GfPlanCardRecord; engineStrategyId?: string | null; gf?: Record<string, unknown>; [key: string]: unknown } | null;
}

/** Choosing a plan: the earlier granny flat plan that carries a card is replaced, and the new one is chosen. */
export function withChosenGfPlan<T extends PlanRecordLike>(prev: readonly T[] | null | undefined, record: T): T[] {
    const others = (prev || []).filter((p) => !(p.type === 'granny_flat' && (p.inputs as { card?: unknown } | null | undefined)?.card));
    return withChosenPlan(others, record);
}

/** The chosen granny flat plan on the file, if one is chosen and carries its card. */
export function chosenGfPlanOf(list: ReadonlyArray<Record<string, unknown>> | null | undefined): ChosenGfPlan | null {
    const hit = (list || []).find((s) => s?.type === 'granny_flat' && s.chosen === true && (s.inputs as { card?: unknown } | undefined)?.card);
    return (hit as ChosenGfPlan | undefined) ?? null;
}

/** The family guide's figures for the chosen plan (the brief carries them, with the plan's title). */
export function gfGuideFiguresOf(plan: ChosenGfPlan | null | undefined): GfSolicitorBriefFigures | null {
    const card = plan?.inputs?.card;
    if (!card || !(card.arrangement.given > 0)) return null;
    return {
        entryContribution: card.arrangement.given,
        interestValue: card.arrangement.interestValue,
        deprivation: card.arrangement.gift,
        isHomeowner: card.arrangement.isHomeowner,
        dateEntered: card.arrangement.dateEntered,
        planTitle: card.words.title,
    };
}

/** The family table frozen for the saved plan (the plan page keeps no projection rows). */
export function gfFamilyRecordOf(
    gi: GfGuaranteedIncome | null,
    factFind: DraftFactFind | null | undefined,
    rows: readonly unknown[] | null | undefined,
    compare: { rows: readonly unknown[] | null | undefined; note: string } | null,
    horizonYears: number,
): GfFamilyRecord | null {
    if (!gi) return null;
    const built = buildSoaGuaranteedIncome({
        factFind, personName: gi.personName, amount: gi.amount, kind: gi.kind, incomeAYear: gi.incomeAYear,
        rows, compareRows: compare?.rows ?? null, compareNote: compare?.note ?? null, horizonYears,
        plan: { estate: 0, pension: 0, cashflow: 0 },
    });
    return built ? { personName: gi.personName, amount: gi.amount, kind: gi.kind, incomeAYear: gi.incomeAYear, compareNote: built.compareNote, rows: built.rows } : null;
}

/** Session key: Home's "Start a new model" — open on an empty step 1 and ask step 3's questions again (one-shot). */
export const GF_START_FRESH_KEY = 'careCompanionGfStartFresh';
